import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/shared/ThemeToggle';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';

/**
 * Password Reset Request Component
 * 
 * Allows users to request a password reset link via email.
 * Features minimalist design matching the login page with wrestling team branding.
 * 
 * Flow:
 * 1. User enters email address
 * 2. Backend sends reset link (if email exists)
 * 3. Success message shown (regardless of email existence for security)
 * 4. User checks email and clicks reset link
 */
const PasswordResetRequest: React.FC = () => {
    const [email, setEmail] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const navigate = useNavigate();
    const { isDark } = useTheme();

    /**
     * Handle form submission
     * 
     * Sends password reset request to backend API.
     * Always shows success message for security (prevents email enumeration).
     */
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const response = await fetch(getApiUrl('/api/password-reset/'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const result = await safeJsonParse(response, 'password reset request');
            
            if (result.success) {
                setSubmitted(true);
            } else {
                setError(result.error || 'Failed to send reset email');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Success screen after submission
    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 cgm-bg-primary transition-colors duration-300">
                {/* Theme Toggle - Fixed Position */}
                <div className="fixed top-4 right-4 z-50">
                    <ThemeToggle />
                </div>

                <div className="max-w-md w-full space-y-8">
                    {/* Header with Wrestling Logo */}
                    <div className="text-center">
                        <img
                            className="mx-auto h-24 w-auto mb-8 transition-all duration-200 hover:scale-110 cursor-pointer"
                            src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                            alt="Wrestling Team Logo"
                            onClick={() => navigate('/')}
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = isDark
                                    ? "/logos/LOGO-MAIN-WHITE.png"
                                    : "/logos/LOGO-MAIN-BLK.png";
                            }}
                        />
                        <h2 className="text-3xl font-light tracking-tight cgm-text-primary mb-2">
                            Check Your Email
                        </h2>
                        <p className="cgm-text-secondary text-sm">
                            Password reset instructions sent
                        </p>
                    </div>

                    {/* Success Message Card */}
                    <div className="cgm-card p-8">
                        <div className="cgm-alert-success px-4 py-3 rounded-md mb-6 text-sm">
                            ✓ Reset link sent successfully
                        </div>

                        <div className="space-y-4 cgm-text-secondary text-sm">
                            <p>
                                If an account exists for <strong className="cgm-text-primary">{email}</strong>, 
                                you will receive a password reset link shortly.
                            </p>
                            
                            <div className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-r">
                                <p className="font-medium cgm-text-primary mb-1">
                                    ⏰ Link expires in 24 hours
                                </p>
                                <p className="text-xs">
                                    For security reasons, the reset link will only work once and expires after 24 hours.
                                </p>
                            </div>

                            <p>
                                Didn't receive an email? Check your spam folder or try again.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-8 space-y-3">
                            <button
                                onClick={() => navigate('/login')}
                                className="cgm-button w-full py-3 px-4"
                            >
                                Return to Login
                            </button>
                            
                            <button
                                onClick={() => {
                                    setSubmitted(false);
                                    setEmail('');
                                }}
                                className="w-full py-3 px-4 cgm-text-secondary hover:cgm-text-primary transition-colors duration-200 text-sm"
                            >
                                Resend Email
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Request form screen
    return (
        <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 cgm-bg-primary transition-colors duration-300">
            {/* Theme Toggle - Fixed Position */}
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="max-w-md w-full space-y-8">
                {/* Header with Wrestling Logo */}
                <div className="text-center">
                    <img
                        className="mx-auto h-24 w-auto mb-8 transition-all duration-200 hover:scale-110 cursor-pointer"
                        src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                        alt="Wrestling Team Logo"
                        onClick={() => navigate('/')}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = isDark
                                ? "/logos/LOGO-MAIN-WHITE.png"
                                : "/logos/LOGO-MAIN-BLK.png";
                        }}
                    />
                    <h2 className="text-3xl font-light tracking-tight cgm-text-primary mb-2">
                        Reset Password
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        Enter your email to receive a reset link
                    </p>
                </div>

                {/* Password Reset Form */}
                <div className="cgm-card p-8">
                    {error && (
                        <div className="cgm-alert-error px-4 py-3 rounded-md mb-6 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium cgm-text-secondary mb-2">
                                Email Address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isLoading}
                                className="cgm-input w-full px-4 py-3"
                                placeholder="Enter your email"
                                autoFocus
                            />
                            <p className="mt-2 text-xs cgm-text-muted">
                                We'll send you a secure link to reset your password
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="cgm-button w-full mt-8 py-3 px-4 flex items-center justify-center"
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                                    Sending...
                                </>
                            ) : (
                                'Send Reset Link'
                            )}
                        </button>
                    </form>

                    {/* Navigation Links */}
                    <div className="mt-6 space-y-4 text-center">
                        <p className="cgm-text-muted text-sm">
                            Remember your password?{' '}
                            <Link
                                to="/login"
                                className="cgm-text-primary hover:cgm-text-secondary font-medium transition-colors duration-200 underline underline-offset-4"
                            >
                                Back to Login
                            </Link>
                        </p>
                        
                        <p className="cgm-text-muted text-sm">
                            Don't have an account?{' '}
                            <Link
                                to="/register"
                                className="cgm-text-primary hover:cgm-text-secondary font-medium transition-colors duration-200 underline underline-offset-4"
                            >
                                Register here
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordResetRequest;

